import React, { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  Box, Grid, Button, Stack, Typography, Chip,
  Accordion, AccordionSummary, AccordionDetails, CircularProgress,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon, PictureAsPdf as PdfIcon, Info as InfoIcon } from "@mui/icons-material";
import { getReportUrl } from "../../../constants/apiUrl";
import { useTranslation } from "react-i18next";
import { useSystemId, useReports } from "../../../hooks/useReduxSelectors";
import { formatDateTime, formatTimeOnly } from "../../../helpers/utils";
import InfoModal from "./InfoModal";
import { fetchReports } from "../../../ducks/reportsDuck";
import ReportSearchBar from "../reportSearchBar/ReportSearchBar";
import ManualRunControls from "../manualRunControls/ManualRunControls";
import AutoZeroPrintVersion from "./AutoZeroPrintVersion";

const stateColor = (state) => {
  if (state === "Warning") return "warning.main";
  if (state?.includes("Failed")) return "error.main";
  return "primary.main";
};

const AutoZeroReport = ({ systemName }) => {
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [infoIndex, setInfoIndex] = useState(-1);
  const dispatch = useDispatch();
  const systemId = useSystemId();
  const reports = useReports();
  const values = reports?.AutoZeroReport?.entries;
  const { t } = useTranslation("common");

  const fetchAutoZeroReport = useCallback(() => {
    const args = { ReportName: "AutoZero", Count: 3 };
    dispatch(
      fetchReports({ systemId, url: getReportUrl, type: "AutoZero", args, t })
    ).then(() => setLoading(false));
  }, [dispatch, t]);

  const onInfoClicked = (idx) => {
    setInfoIndex(idx);
    setIsModalOpen(true);
  };

  const formatDuration = (seconds) =>
    Math.floor(seconds / 60) + "m " + Math.floor(seconds % 60) + "s";

  return (
    <>
      <AutoZeroPrintVersion
        isOpen={isPrintDialogOpen}
        onClose={() => setIsPrintDialogOpen(false)}
        values={values}
        systemName={systemName}
        loading={loading}
        t={t}
      />
      <Accordion
        disableGutters
        onChange={(e, expanded) => { if (expanded) fetchAutoZeroReport(); }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "common.white" }} />}
          sx={{ bgcolor: "primary.main", color: "common.white" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>AutoZero History</Typography>
            <ManualRunControls reportName="AutoZero" />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 1.5 }}>
          <ReportSearchBar reportName="AutoZero" openPrintDialog={() => setIsPrintDialogOpen(true)} />

          {/* <Stack direction="row" spacing={1} sx={{ px: 1.5, pb: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PdfIcon />}
              onClick={() => setIsPrintDialogOpen(true)}
            >
              {t("Download PDF")}
            </Button>
          </Stack> */}

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && (
            <InfoModal
              values={values}
              show={isModalOpen}
              onHide={() => setIsModalOpen(false)}
              infoIndex={infoIndex}
              setInfoIndex={setInfoIndex}
              t={t}
            />
          )}

          <Stack spacing={1.5} sx={{ px: 0.5 }}>
            {!loading && values?.length > 0 && values.map((val, idx) => (
              <Box
                key={`az-${idx}`}
                sx={{ bgcolor: "background.mid", borderRadius: 1, overflow: "hidden" }}
              >
                {/* Status accent */}
                <Box sx={{ height: 3, bgcolor: stateColor(val.State) }} />

                <Box sx={{ p: 1.5 }}>
                  {/* Header row */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="subtitle2">{formatDateTime(val.ReportTimeStamp)}</Typography>
                      <Chip
                        label={t(val.State)}
                        size="small"
                        sx={{ bgcolor: stateColor(val.State), color: "common.white", fontWeight: 600 }}
                      />
                    </Stack>
                    <Button
                      size="small"
                      variant={infoIndex === idx ? "contained" : "outlined"}
                      color={infoIndex === idx ? "warning" : "primary"}
                      startIcon={<InfoIcon />}
                      onClick={() => onInfoClicked(idx)}
                    >
                      Info
                    </Button>
                  </Stack>

                  {/* Meta info */}
                  <Stack direction="row" spacing={3} sx={{ mb: 1.5, color: "text.secondary" }}>
                    <Typography variant="body2">
                      {t("Started")}: {formatTimeOnly(val.StartTime)}
                    </Typography>
                    <Typography variant="body2">
                      {t("Duration")}: {formatDuration(val.Duration)}
                    </Typography>
                  </Stack>

                  {/* Gas results */}
                  {val.ComponentResults && Object.keys(val.ComponentResults).length > 0 && (
                    <Grid container spacing={1}>
                      {Object.entries(val.ComponentResults).map(([gas, data]) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={gas}>
                          <Box sx={{ bgcolor: "background.dark", borderRadius: 0.5, p: 1 }}>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                              {gas}
                            </Typography>
                            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                              <Stack alignItems="flex-start">
                                <Typography variant="caption" sx={{ color: "text.disabled", lineHeight: 1 }}>
                                  {t("PreCal")}
                                </Typography>
                                <Typography variant="body2">
                                  {data.PreCalValue.toFixed(2)} {data.Unit}
                                </Typography>
                              </Stack>
                              <Stack alignItems="center">
                                <Typography variant="caption" sx={{ color: "text.disabled", lineHeight: 1 }}>
                                  {t("Delta")}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {(data.PostCalValue - data.PreCalValue).toFixed(2)} {data.Unit}
                                </Typography>
                              </Stack>
                              <Stack alignItems="flex-end">
                                <Typography variant="caption" sx={{ color: "text.disabled", lineHeight: 1 }}>
                                  {t("PostCal")}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.disabled" }}>
                                  {data.PostCalValue.toFixed(2)} {data.Unit}
                                </Typography>
                              </Stack>
                            </Stack>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default AutoZeroReport;
